/* global API_URL, dataLayer, MOCK_GOOGLE_SSO, MOCK_APPLE_SSO, MOCK_OTP_AUTH */
import _, { has as hasProperty } from 'lodash'
import { scroller } from 'react-scroll'
import { cookie } from 'reduxModules/utils/cookie'

import logger from 'components/utils/logger'
import { navHandlers } from 'reduxModules/app/dispatchers'
import { dispatchDisplayRecaptcha } from 'reduxModules/captcha/dispatchers'
import modalDispatchers from 'reduxModules/generic/modal/dispatchers'
import { initConfirmation } from 'reduxModules/confirmation'
import {
  openProfile,
  closeProfile,
  openRetentionDialogAction,
  closeRetentionDialogAction,
  acceptRetentionDiscount,
  types as userTypes,
  disableRetentionDialogSuccess,
  enableRetentionDialogSuccess
} from 'reduxModules/users'
import { cartItemsFetch } from 'reduxModules/cart'
import types from 'reduxModules/auth/types'
import { subscriptionSummaryFetch } from 'reduxModules/subscriptions'
import dispatchPayNow from 'reduxModules/subscriptions/payNow'
import { clearRecaptchaToken, sendUTM } from 'reduxModules/app'
import { homeFetchCurrent, homeFetchCurrentMore } from 'reduxModules/home'
import { navigate } from '../../../utils/navigation'
import { OTP_LOGIN_PATH, otpLoginState } from '../../../utils/otpRoute'
import pushGa4Event from '../../../utils/pushGa4Event'
import {
  captureGa4Identifiers,
  resetGa4Capture
} from '../../../utils/ga4Identifiers'

import {
  changePassword,
  forgotPassword,
  resetPassword,
  loginWithFacebook,
  loginWithMicrosoft,
  loginWithGoogle,
  loginWithGoogleRequestSuccess,
  loginWithApple,
  loginWithAppleRequestSuccess,
  login,
  loginRequestSuccess,
  proxyLogin,
  openAthensLogin,
  signUp,
  cancelSubscription,
  clearSubscription,
  markSubscriptionCancellationPending,
  getUser,
  revokeFreeTrialPermission,
  updateGiftAvailability,
  deleteAccount,
  downgradeSubscription,
  otpRequestSuccess,
  requestOtp,
  otpVerifyFailure,
  verifyOtp
} from './'
import {
  OTP_MOCK_VALID_CODE,
  otpMockAccountRequestResponse,
  otpMockRequestResponse,
  otpMockVerifyResponse,
  otpMockVerifyFailure
} from './otpMock'
import notifications, {
  toastError,
  toastFailure,
  toastRestrictedAccount,
  toastServerError,
  toastSuccess
} from '../../../global/notifications'
import googleMockAuthResponse from './googleMock'
import appleMockAuthResponse from './appleMock'
import {
  accountConsentPayload,
  signupSurfacePayload,
  accountConsentEventProps
} from '../../../utils/accountConsent'
import { isRestrictedAccount } from '../../../utils/restrictedAccount'

const sendUTMCallback = async (dispatch) => {
  const result = {}
  // TODO: change to let after fix the original problem
  // result = await retrieveAdData(dispatch)
  if (!_.isEmpty(result))
    dispatch(sendUTM(result.utm)).then(([_, res]) => {
      if (res.type === 'SET_UTM_SUCCESS')
        Object.keys(res.payload).forEach((value) => {
          dispatch(cookie(`utm_${value}`, null, { path: '/' }))
        })
    })
}
// SPEC-027 D2: refresh GA4 capture on login/logout transitions so the cached
// session attributes are current for subsequent events.
//
// Trade-off (review S-1): resetGa4Capture only clears the in-flight capture
// token, not the already-cached `captured` values, and the captureGa4Identifiers
// re-run below is fire-and-forget. So the immediate pushGa4Event for the login
// event itself still reads the previous client_id/session_id; only later events
// observe the refreshed triple. This is intentional: the browser's `_ga` cookie
// (client_id) does not change across login, and a stale-but-real session_id is
// more useful than the user_id fallback we'd get by fully clearing the cache.
const refreshGa4Capture = () => {
  resetGa4Capture()
  captureGa4Identifiers()
}

const afterLoginCallback = (
  dispatch,
  props,
  subscription = {},
  inCheckout = false,
  target = ''
) => {
  const location = props?.location
  const next = props?.next || location?.state?.next

  if (_.isNumber(subscription))
    dispatch(subscriptionSummaryFetch(subscription)())

  dispatch(cartItemsFetch())
  // google optimize event
  dataLayer.push({ isLoggedIn: true })

  if (next) navigate(next)
  else if (!inCheckout) {
    const isFullUrl = (url) => /^(http|https):\/\//.test(url)

    if (isFullUrl(target))
      // If the target is a full URL, use window.location to navigate
      window.location.href = target
    // If it's a relative path, use navigation utility
    else navigate(target || '/')
  }
}

// Resolves { success, offerOtp }: a rejected password unlocks the one-time
// code alternative (SPEC-041 FR-3), which LoginBox offers from its own state.
export const dispatchLogin = (dispatch, data, props) =>
  dispatch(
    login(data.recaptcha)({ email: data.email, password: data.password })
  )
    .then(async (res) => {
      if (res[1].type === types.AUTH_LOGIN_SUCCESS) {
        refreshGa4Capture()
        pushGa4Event(
          'GTMEvent',
          {
            eventCategory: 'account-sign-in',
            eventAction: 'form-completion',
            eventLabel: 'email',
            method: 'password'
          },
          res[1].payload.user.id
        )

        const { isMobile, inCheckout } = props
        const subscription = _.has(res[1], 'payload.user.subscription')
          ? res[1].payload.user.subscription
          : {}

        if (
          inCheckout &&
          hasProperty(res[1].payload, 'user.role') &&
          res[1].payload.user.role === 'free_library_subscriber'
        ) {
          navigate('/')
          toastError(notifications.unableToPurchase)
        } else afterLoginCallback(dispatch, props, subscription, inCheckout)
        if (isMobile) scroller.scrollTo('shipTo', { offset: 64, smooth: true })
        toastSuccess(notifications.login)
        if (subscription) sendUTMCallback(dispatch)
        return { success: true }
      }
      if (res[1]?.payload?.status === 412) {
        toastError(notifications.recaptcha)
        dispatchDisplayRecaptcha(dispatch)
        return { success: false }
      }
      // SPEC-119 D3: a terminated account that proved ownership gets the
      // restricted notice from the form, and nothing else — no code offer
      if (isRestrictedAccount(res[1].payload))
        return { success: false, restricted: true }
      toastFailure(notifications.login, res[1].payload)
      pushGa4Event('GTMEvent', {
        eventCategory: 'account-otp',
        eventAction: 'offered',
        eventLabel: 'login'
      })
      return { success: false, offerOtp: true }
    })
    .catch(() => {
      toastError(notifications.login)
      return { success: false }
    })
    .finally(() => dispatch(clearRecaptchaToken()))

export const dispatchProxyLogin = (dispatch, data, props) => {
  dispatch(proxyLogin({ ...data }))
    .then(async (res) => {
      if (res[1].type === types.AUTH_LOGIN_SUCCESS) {
        refreshGa4Capture()
        pushGa4Event(
          'GTMEvent',
          {
            eventCategory: 'account-sign-in',
            eventAction: 'form-completion',
            eventLabel: 'email'
          },
          res[1].payload.user.id
        )

        const { isMobile, inCheckout } = props
        const subscription = _.has(res[1], 'payload.user.subscription')
          ? res[1].payload.user.subscription
          : {}

        if (
          inCheckout &&
          hasProperty(res[1].payload, 'user.role') &&
          res[1].payload.user.role === 'free_library_subscriber'
        ) {
          navigate('/')
          toastError(notifications.unableToPurchase)
        } else afterLoginCallback(dispatch, props, subscription, inCheckout)
        if (isMobile) scroller.scrollTo('shipTo', { offset: 64, smooth: true })
        toastSuccess(notifications.login)
      } else if (isRestrictedAccount(res[1].payload)) toastRestrictedAccount()
      else toastFailure(notifications.login, res[1].payload)
    })
    .catch((payload) => {
      const error = payload.errors
      toastServerError(error)
    })
}

// RegisterBox's social buttons say "Register with …" (capital R); the old
// case-sensitive includes('register') logged every one as a sign-in. The
// SPEC-101 tiles say so with `signUp`.
const isRegisterButton = (props) =>
  Boolean(props && props.signUp) ||
  /register/i.test((props && props.text) || '')

// account-creation vs account-sign-in. One click can do either, so the
// backend's `created` flag wins when it sends one; otherwise the screen the
// button sits on decides.
const accountEventCategory = (props, payload) => {
  const created = payload && payload.created
  if (typeof created === 'boolean')
    return created ? 'account-creation' : 'account-sign-in'
  return isRegisterButton(props) ? 'account-creation' : 'account-sign-in'
}

// The same for an email code (SPEC-101): without the flag, the flow the
// request step announced decides
const accountCreatedByCode = (flow, payload) =>
  typeof payload?.created === 'boolean' ? payload.created : flow === 'signup'

export const dispatchSignUp = (dispatch, data, props) => {
  // SPEC-099: RegisterBox always has an answer for the newsletter box
  const marketingConsent = Boolean(data.marketingConsent)
  const consentTouched = Boolean(data.marketingConsentTouched)
  dispatch(
    signUp(data.recaptcha)({
      email: data.email,
      password1: data.password1,
      ...accountConsentPayload(marketingConsent, consentTouched)
    })
  )
    .then(([_, res]) => {
      if (res.type === types.AUTH_SIGNUP_SUCCESS) {
        const { inCheckout, isMobile, location } = props
        refreshGa4Capture()
        pushGa4Event(
          'GTMEvent',
          {
            eventCategory: 'account-creation',
            eventAction: 'form-completion',
            eventLabel: 'email',
            method: 'email',
            ...accountConsentEventProps(marketingConsent, consentTouched)
          },
          res.payload.user.id
        )
        toastSuccess(notifications.signup)

        if (hasProperty(location, 'query.forum'))
          window.location.href = `${API_URL}/accounts/login/?next=/forum/return-forum`
        else if (!inCheckout) navigate('/')
        else if (isMobile)
          scroller.scrollTo('shipTo', { offset: 64, smooth: true })
        dataLayer.push({ isLoggedIn: true })
      } else if (res?.payload?.status === 412) {
        toastError(notifications.recaptcha)
        dispatchDisplayRecaptcha(dispatch)
      } else if (res?.payload?.value?.error_code === 'email_exists') {
        // SPEC-041 FR-2: the email already has an account. Instead of the
        // dead-end toast, hand off to the one-time code login page, which
        // asks before sending anything. (Failure bodies are the raw backend
        // JSON, hence snake_case error_code.)
        pushGa4Event('GTMEvent', {
          eventCategory: 'account-otp',
          eventAction: 'offered',
          eventLabel: 'register'
        })
        navigate(OTP_LOGIN_PATH, {
          state: otpLoginState({
            email: data.email,
            trigger: 'register',
            inCheckout: Boolean(props.inCheckout),
            location: props.location || window.location
          })
        })
      } else toastFailure(notifications.signup, res.payload)
    })
    .catch((payload) => {
      const error = payload.errors
      toastServerError(error)
    })
    .finally(() => dispatch(clearRecaptchaToken()))
}

export const dispatchFacebookLogin = (dispatch, accessToken, props) =>
  dispatch(
    loginWithFacebook({
      access_token: accessToken,
      ...accountConsentPayload(
        props.marketingConsent,
        props.marketingConsentTouched
      ),
      ...signupSurfacePayload(props.signupSurface)
    })
  ).then((res) => {
    if (res[1].type === types.AUTH_FACEBOOK_LOGIN_SUCCESS) {
      try {
        refreshGa4Capture()
        pushGa4Event(
          'GTMEvent',
          {
            eventCategory: accountEventCategory(props, res[1].payload),
            eventAction: 'form-completion',
            eventLabel: 'facebook',
            method: 'facebook',
            ...accountConsentEventProps(
              props.marketingConsent,
              props.marketingConsentTouched
            )
          },
          res[1].payload.user.id
        )
      } catch (e) {
        logger.error('An error occurred after user signed in using Facebook', {
          error: e
        })
        throw e
      }
      const subscription = _.has(res[1], 'payload.user.subscription')
        ? res[1].payload.user.subscription
        : {}
      const { inCheckout } = props
      afterLoginCallback(dispatch, props, subscription, inCheckout)
      toastSuccess(notifications.login)
      if (subscription) sendUTMCallback(dispatch)
    } else if (isRestrictedAccount(res[1].payload)) toastRestrictedAccount()
    else toastFailure(notifications.login, res[1].payload)
  })

export const dispatchMicrosoftLogin = (dispatch, accessToken, props) =>
  dispatch(loginWithMicrosoft({ access_token: accessToken })).then((res) => {
    if (res[1].type === types.AUTH_MICROSOFT_LOGIN_SUCCESS) {
      try {
        refreshGa4Capture()
        pushGa4Event(
          'GTMEvent',
          {
            eventCategory: accountEventCategory(props, res[1].payload),
            eventAction: 'form-completion',
            eventLabel: 'microsoft',
            method: 'microsoft',
            ...accountConsentEventProps(
              props.marketingConsent,
              props.marketingConsentTouched
            )
          },
          res[1].payload.user.id
        )
      } catch (e) {
        logger.error('An error occurred after user signed in using Microsoft', {
          error: e
        })
        throw e
      }
      const subscription = _.has(res[1], 'payload.user.subscription')
        ? res[1].payload.user.subscription
        : {}
      const { inCheckout } = props
      afterLoginCallback(dispatch, props, subscription, inCheckout)
      toastSuccess(notifications.login)
    } else if (isRestrictedAccount(res[1].payload)) toastRestrictedAccount()
    else toastFailure(notifications.login, res[1].payload)
  })

export const dispatchGoogleLogin = (dispatch, credential, props) => {
  // The MOCK branch lives here, not inside loginWithGoogle: a fetchHelper-built
  // action creator can't intercept before the network from the inside. The mock
  // must DISPATCH the success action (exactly as fetchHelper does at
  // fetch.js:104) so the reducer runs and the session persists — returning the
  // tuple alone would leave the user logged out. Reuse the real action creator
  // so the mock payload can't drift from the live shape. Going live = flip
  // MOCK_GOOGLE_SSO to 0; delete this branch as cleanup.
  const exchange = MOCK_GOOGLE_SSO
    ? (() => {
        const success = loginWithGoogleRequestSuccess(googleMockAuthResponse)
        dispatch(success)
        return Promise.resolve([{ type: 'HIDDEN_FETCHING' }, success])
      })()
    : dispatch(
        loginWithGoogle({
          // SPEC-101 FR-4: the tiles send the popup's auth code ({ code });
          // Google's own button sends its id_token string
          ...(typeof credential === 'string'
            ? { id_token: credential }
            : credential),
          ...accountConsentPayload(
            props.marketingConsent,
            props.marketingConsentTouched
          ),
          ...signupSurfacePayload(props.signupSurface)
        })
      )

  return exchange.then((res) => {
    if (res[1].type === types.AUTH_GOOGLE_LOGIN_SUCCESS) {
      try {
        refreshGa4Capture()
        pushGa4Event(
          'GTMEvent',
          {
            eventCategory: accountEventCategory(props, res[1].payload),
            eventAction: 'form-completion',
            eventLabel: 'google',
            method: 'google',
            ...accountConsentEventProps(
              props.marketingConsent,
              props.marketingConsentTouched
            )
          },
          res[1].payload.user.id
        )
      } catch (e) {
        logger.error('An error occurred after user signed in using Google', {
          error: e
        })
        throw e
      }
      const subscription = _.has(res[1], 'payload.user.subscription')
        ? res[1].payload.user.subscription
        : {}
      const { inCheckout } = props
      afterLoginCallback(dispatch, props, subscription, inCheckout)
      toastSuccess(notifications.login)
    } else if (isRestrictedAccount(res[1].payload)) toastRestrictedAccount()
    else toastFailure(notifications.login, res[1].payload)
  })
}

export const dispatchAppleLogin = (dispatch, credential, user, props) => {
  // The MOCK branch lives here, not inside loginWithApple: a fetchHelper-built
  // action creator can't intercept before the network from the inside. The mock
  // must DISPATCH the success action (exactly as fetchHelper does at
  // fetch.js:104) so the reducer runs and the session persists — returning the
  // tuple alone would leave the user logged out. Reuse the real action creator
  // so the mock payload can't drift from the live shape. Going live = flip
  // MOCK_APPLE_SSO to 0; delete this branch as cleanup.
  const exchange = MOCK_APPLE_SSO
    ? (() => {
        const success = loginWithAppleRequestSuccess(appleMockAuthResponse)
        dispatch(success)
        return Promise.resolve([{ type: 'HIDDEN_FETCHING' }, success])
      })()
    : // D4: Apple returns the member's name/email only on the FIRST
      // authorization and never again, so forward `user` when (and only when)
      // present. Omitting the key on returning logins keeps the payload clean.
      dispatch(
        loginWithApple({
          id_token: credential,
          ...(user && { user }),
          ...accountConsentPayload(
            props.marketingConsent,
            props.marketingConsentTouched
          ),
          ...signupSurfacePayload(props.signupSurface)
        })
      )

  return exchange.then((res) => {
    if (res[1].type === types.AUTH_APPLE_LOGIN_SUCCESS) {
      try {
        refreshGa4Capture()
        pushGa4Event(
          'GTMEvent',
          {
            eventCategory: accountEventCategory(props, res[1].payload),
            eventAction: 'form-completion',
            eventLabel: 'apple',
            method: 'apple',
            ...accountConsentEventProps(
              props.marketingConsent,
              props.marketingConsentTouched
            )
          },
          res[1].payload.user.id
        )
      } catch (e) {
        logger.error('An error occurred after user signed in using Apple', {
          error: e
        })
        throw e
      }
      const subscription = _.has(res[1], 'payload.user.subscription')
        ? res[1].payload.user.subscription
        : {}
      const { inCheckout } = props
      afterLoginCallback(dispatch, props, subscription, inCheckout)
      toastSuccess(notifications.login)
    } else if (isRestrictedAccount(res[1].payload)) toastRestrictedAccount()
    else toastFailure(notifications.login, res[1].payload)
  })
}

export const dispatchForgotPassword = (dispatch, data) =>
  dispatch(forgotPassword(data)).then(([_, res]) => {
    if (res.type === types.AUTH_FORGOT_PASSWORD_SUCCESS) {
      toastSuccess(notifications.forgotPassword)
      navigate('/')
    } else toastFailure(notifications.forgotPassword, res.payload)
  })

// SPEC-101 FR-7: a no-password account sets one through the reset email
// (the backend allows it since SPEC-100); unlike Forgot password, the user
// stays on their account page
export const dispatchSetPassword = (dispatch, email) =>
  dispatch(forgotPassword({ email })).then(([_, res]) => {
    if (res.type === types.AUTH_FORGOT_PASSWORD_SUCCESS)
      toastSuccess(notifications.setPassword)
    else toastFailure(notifications.setPassword, res.payload)
  })

// Resolves { success } — or { success: false, restricted: true } when the
// account was terminated (SPEC-119), so the page can show the notice.
export const dispatchResetPassword = (dispatch, data, props) => {
  const payload = {
    new_password1: data.password1,
    new_password2: data.password2,
    token: props.params.token,
    uid: props.params.uid
  }
  return dispatch(resetPassword(payload))
    .then(([_, res]) => {
      if (res.type === types.AUTH_RESET_PASSWORD_SUCCESS) {
        // The confirm now answers with the login payload: the reset IS the
        // sign-in (Pedro, 2026-10-22), so the session starts here like any
        // other login. An older answer without it keeps today's behaviour.
        const { token, user } = res.payload || {}
        if (token && user) {
          dispatch(loginRequestSuccess({ token, user }))
          refreshGa4Capture()
          toastSuccess(notifications.passwordReset)
        } else toastSuccess(notifications.passwordChange)
        navigate('/')
        return { success: true }
      }
      // SPEC-119 D3: a terminated account proving ownership through the
      // reset link gets the restricted notice from the page, nothing else
      if (isRestrictedAccount(res.payload))
        return { success: false, restricted: true }
      // Same message family as change password: keep the API's reason
      // (expired/invalid token, rejected password) over the canned copy
      toastFailure(notifications.passwordChange, res.payload)
      return { success: false }
    })
    .catch((payload) => {
      const error = payload.errors
      toastServerError(error)
      return { success: false }
    })
}

export const dispatchChangePassword = (dispatch, data) => {
  const payload = {
    new_password1: data.password1,
    new_password2: data.password2,
    old_password: data.oldPassword
  }
  dispatch(changePassword(payload))
    .then(([_, res]) => {
      if (res.type === types.AUTH_CHANGE_PASSWORD_SUCCESS) {
        toastSuccess(notifications.passwordChange)
        navigate('/')
      } else
        // Surface the API's reason (e.g. "Your old password was entered
        // incorrectly.") with the canned copy only as fallback (audit §2)
        toastFailure(notifications.passwordChange, res.payload)
    })
    .catch((payload) => {
      const error = payload.errors
      toastServerError(error)
    })
}

export const onChangePasswordDispatch = (dispatch) => ({
  onSubmit: (data) => dispatchChangePassword(dispatch, data),
  ...navHandlers(dispatch)
})

export const onForgotPasswordDispatch = (dispatch) => ({
  onSubmit: (data) => dispatchForgotPassword(dispatch, data),
  ...navHandlers(dispatch)
})

export const onResetPasswordDispatch = (dispatch, props) => ({
  onSubmit: (data) => dispatchResetPassword(dispatch, data, props),
  ...navHandlers(dispatch)
})

export const onLoginBoxDispatch = (stateProps, dispatchProps, ownProps) => {
  const { dispatch } = dispatchProps
  return {
    ...stateProps,
    ...ownProps,
    onSubmit: (data) =>
      dispatchLogin(
        dispatch,
        { ...data, recaptcha: stateProps.recaptcha },
        ownProps
      )
  }
}

export const onAuthDispatch = (dispatch) => ({
  ...navHandlers(dispatch)
})

export const onSignupBoxDispatch = (stateProps, dispatchProps, ownProps) => {
  const { dispatch } = dispatchProps
  return {
    ...stateProps,
    ...ownProps,
    onSubmit: (data) =>
      dispatchSignUp(
        dispatch,
        { ...data, recaptcha: stateProps.recaptcha },
        ownProps
      )
  }
}

export const facebookLoginCallbackDispatch = (dispatch, props) => ({
  facebookLoginCallback: (response) =>
    dispatchFacebookLogin(dispatch, response.accessToken, props)
})

export const microsoftLoginCallbackDispatch = (dispatch, props) => ({
  microsoftLoginCallback: (response) =>
    dispatchMicrosoftLogin(dispatch, response.accessToken, props)
})

export const googleLoginCallbackDispatch = (dispatch, props) => ({
  googleLoginCallback: (response) =>
    dispatchGoogleLogin(dispatch, response.credential, props)
})

export const appleLoginCallbackDispatch = (dispatch, props) => ({
  // Apple's onSuccess response nests the id_token under `authorization` and
  // carries the first-login-only profile under `user` (D4); forward both.
  appleLoginCallback: (response) =>
    dispatchAppleLogin(
      dispatch,
      response.authorization.id_token,
      response.user,
      props
    )
})

export const accountDispatch = (dispatch) => ({
  // SPEC-098 FR-2: resolves { success } or { success: false, message }
  payNow: (subscription) => dispatchPayNow(dispatch, subscription),
  dispatchSetPassword: (email) => dispatchSetPassword(dispatch, email),
  dispatchCancelSubscription: (subscription) => {
    const { cancellationDiscountEligible, cancellationDiscountPercentage } =
      subscription
    const confirmation = {
      title: 'Sorry to see you go',
      text: 'What makes you cancel?',
      options: [
        { resolveValue: 'accept', text: 'Continue', class: 'confirm-btn' }
      ],
      feedback: {
        options: [
          {
            text: 'Site speed too slow',
            value: 'Site speed too slow'
          },
          {
            text: 'Wanted to check us out',
            value: 'Wanted to check us out'
          },
          {
            text: "Can't afford it",
            value: "Can't afford it"
          },
          {
            text: 'Not enough value',
            value: 'Not enough value'
          },
          {
            text: 'Not enough time',
            value: 'Not enough time'
          },
          {
            text: 'Technical issue',
            value: 'Technical issue'
          },
          {
            text: 'Subscription plan upgrade',
            value: 'Subscription plan upgrade'
          },
          {
            text: 'Subscription plan downgrade',
            value: 'Subscription plan downgrade'
          },
          {
            text: 'Not using',
            value: 'Not using'
          },
          {
            text: 'Used what I needed',
            value: 'Used what I needed'
          },
          {
            text: 'No longer working in the field',
            value: 'No longer working in the field'
          },
          {
            text: 'Not what I thought I was getting',
            value: 'Not what I thought I was getting'
          },
          {
            text: 'Free trial ended',
            value: 'Free trial ended'
          },
          {
            text: 'Not enough courses in my field',
            value: 'Not enough courses in my field'
          },
          {
            text: 'Other',
            value: 'Other'
          }
        ]
      },
      height: 472,
      componentId: 'cancellation'
    }
    dispatch(initConfirmation(confirmation)).on('accept', (feedback) => {
      // Carry the collected reason into the retention dialog so a
      // "Continue to cancel" from there still reports it (SPEC-038) —
      // routing through the offer used to silently drop it.
      const retainedFeedback = _.isEmpty(feedback) ? null : feedback
      if (cancellationDiscountEligible && cancellationDiscountPercentage > 0)
        dispatch(openRetentionDialogAction(retainedFeedback))
      else if (subscription.discountPercentage > 0)
        dispatch(openRetentionDialogAction(retainedFeedback))
      else
        dispatch(
          cancelSubscription({ ...(!_.isEmpty(feedback) && { feedback }) })
        )
          .then(([_, res]) => {
            if (res.type === types.AUTH_CANCEL_SUBSCRIPTION_SUCCESS) {
              if (!res.payload.pendingChange) dispatch(clearSubscription())
              else dispatch(markSubscriptionCancellationPending())
              toastSuccess(notifications.cancelSubscription)
            } else toastFailure(notifications.cancelSubscription, res.payload)
          })
          .catch(() => toastError(notifications.cancelSubscription))
    })
  },
  dispatchDirectCancelSubscription: (feedback) => {
    dispatch(cancelSubscription({ ...(feedback && { feedback }) }))
      .then(([_, res]) => {
        if (res.type === types.AUTH_CANCEL_SUBSCRIPTION_SUCCESS) {
          if (!res.payload.pendingChange) dispatch(clearSubscription())
          else dispatch(markSubscriptionCancellationPending())
          toastSuccess(notifications.cancelSubscription)
        } else toastFailure(notifications.cancelSubscription, res.payload)
      })
      .catch(() => toastError(notifications.cancelSubscription))
  },
  dispatchCancelFreeTrial: (forPromo = false, alreadyEligible = false) => {
    const regularCancelationCopy = {
      title: 'Cancel free trial',
      text: 'This will charge your payment method, do you want to cancel your free trial?',
      options: [
        { resolveValue: 'accept', text: 'Continue', class: 'confirm-btn' }
      ]
    }
    const promoCancellationCopy = {
      title: 'Payment confirmation',
      text: 'This will charge your payment method. Do you want to pay for your subscription to be able to gift a matching subscription?',
      options: [
        { resolveValue: 'accept', text: 'Confirm', class: 'confirm-btn' }
      ]
    }
    const confirmation = forPromo
      ? promoCancellationCopy
      : regularCancelationCopy

    // couldGiftSubscription users must pay before they can gift: SPEC-103
    // D7, the trial ends by paying now (pay_now, which also toasts the
    // charge); a decline keeps the trial, shows the reason and changes
    // nothing. Users who are already eligible (canGiftSubscription) skip
    // the payment dialog and go straight to gifting.
    if (!alreadyEligible)
      dispatch(initConfirmation(confirmation)).on('accept', () =>
        dispatchPayNow(dispatch).then(({ success, message }) => {
          if (!success) {
            toastServerError(message)
            return
          }
          dispatch(revokeFreeTrialPermission())
          if (forPromo) {
            toastSuccess(notifications.enabledToGift)
            dispatch(updateGiftAvailability())
            navigate('/gift_subscription')
          }
        })
      )
    else navigate('/gift_subscription')
  },
  ...modalDispatchers(dispatch),
  openProfileSection() {
    dispatch(openProfile())
  },
  closeProfileSection() {
    dispatch(closeProfile())
  },
  downgradeSubscription() {
    dispatch(downgradeSubscription({ downgrade: true }))
      .then(([_, res]) => {
        if (res.type === types.AUTH_DOWNGRADE_SUBSCRIPTION_SUCCESS) {
          dispatch(getUser())
          toastSuccess(notifications.downgradeSubscription)
        } else toastFailure(notifications.downgradeSubscription, res.payload)
      })
      .catch(() => toastError(notifications.downgradeSubscription))
  },
  closeRetentionDialog() {
    dispatch(closeRetentionDialogAction())
  },
  acceptRetentionDiscount: () => {
    dispatch(disableRetentionDialogSuccess())
    dispatch(acceptRetentionDiscount())
      .then(([_, res]) => {
        if (
          res.type === userTypes.USERS_PROFILE_ACCEPT_RETENTION_DISCOUNT_SUCCESS
        ) {
          toastSuccess(notifications.acceptRetentionDiscount)
          dispatch(closeRetentionDialogAction())
        } else toastFailure(notifications.acceptRetentionDiscount, res.payload)
      })
      .catch((payload) => {
        const error = payload.errors
        toastServerError(error)
      })
      .finally(() => dispatch(enableRetentionDialogSuccess()))
  }
})

export const accountMenuDispatcher = (dispatch) => ({
  getUserInfo: () => dispatch(getUser())
})

export const WATCH_HISTORY_PAGE_SIZE = 12

export const watchHistoryDispatcher = (dispatch) => ({
  loadWatchHistory() {
    dispatch(
      homeFetchCurrent({
        auto_generated: true,
        full: true,
        ordering: '-id',
        limit: WATCH_HISTORY_PAGE_SIZE
      })()
    )
  },
  loadMoreWatchHistory(offset) {
    dispatch(
      homeFetchCurrentMore({
        auto_generated: true,
        full: true,
        ordering: '-id',
        limit: WATCH_HISTORY_PAGE_SIZE,
        offset
      })()
    )
  }
})

export const dispatchOpenAthensLogin = (dispatch, data, props) => {
  dispatch(openAthensLogin({ ...data }))
    .then(async (res) => {
      const target = props.location?.query?.target
        ? decodeURIComponent(props.location?.query?.target)
        : ''
      if (res[1].type === types.AUTH_LOGIN_SUCCESS) {
        refreshGa4Capture()
        pushGa4Event(
          'GTMEvent',
          {
            eventCategory: 'account-sign-in',
            eventAction: 'form-completion',
            eventLabel: 'email'
          },
          res[1].payload.user.id
        )

        const { isMobile, inCheckout } = props
        const subscription = _.has(res[1], 'payload.user.subscription')
          ? res[1].payload.user.subscription
          : {}

        if (
          inCheckout &&
          hasProperty(res[1].payload, 'user.role') &&
          res[1].payload.user.role === 'free_library_subscriber'
        ) {
          navigate('/')
          toastError(notifications.unableToPurchase)
        } else
          afterLoginCallback(dispatch, props, subscription, inCheckout, target)
        if (isMobile) scroller.scrollTo('shipTo', { offset: 64, smooth: true })
        toastSuccess(notifications.login)
      } else if (isRestrictedAccount(res[1].payload)) toastRestrictedAccount()
      else toastFailure(notifications.login, res[1].payload)
    })
    .catch((payload) => {
      const error = payload.errors
      toastServerError(error)
    })
}

export const deleteAccountDispatch = (dispatch) => ({
  deleteAccountAction: () => {
    dispatch(deleteAccount())
      .then(([_, res]) => {
        if (res.type === types.AUTH_ACCOUNT_DELETE_SUCCESS) {
          toastSuccess(notifications.deleteAccount)
          dataLayer.push({ isLoggedIn: false })
          refreshGa4Capture()
          navigate('/')
        } else toastFailure(notifications.deleteAccount, res.payload)
      })
      .catch((payload) => {
        const error = payload.errors
        toastServerError(error)
      })
  }
})

// ---------------------------------------------------------------------------
// SPEC-041: OTP recovery for sign-up and login dead-ends.

// Request a one-time code for the flow's email. The MOCK branch follows the
// SSO-mock lifecycle (dispatch the real action so downstream is identical;
// delete once the backend endpoint ships). Resolves true when the code was
// (nominally) sent — the backend always answers generic success, so "sent"
// carries no account-existence signal. The toast shows the backend's own
// wording ("If an account exists for this email…") when it sends one.
export const dispatchRequestOtp = (dispatch, { email, recaptcha }) => {
  const exchange = MOCK_OTP_AUTH
    ? (() => {
        const success = otpRequestSuccess(otpMockRequestResponse)
        dispatch(success)
        return Promise.resolve([{ type: 'HIDDEN_FETCHING' }, success])
      })()
    : dispatch(requestOtp(recaptcha)({ email }))

  return exchange
    .then(([_, res]) => {
      if (res.type === types.AUTH_OTP_REQUEST_SUCCESS) {
        pushGa4Event('GTMEvent', {
          eventCategory: 'account-otp',
          eventAction: 'requested',
          eventLabel: 'email'
        })
        toastSuccess({
          success: {
            id: notifications.otpRequest.success.id,
            message:
              res.payload?.detail || notifications.otpRequest.success.message
          }
        })
        return true
      }
      if (res?.payload?.status === 412) {
        toastError(notifications.recaptcha)
        dispatchDisplayRecaptcha(dispatch)
      } else toastFailure(notifications.otpRequest, res.payload)
      return false
    })
    .catch(() => {
      toastError(notifications.otpRequest)
      return false
    })
    .finally(() => dispatch(clearRecaptchaToken()))
}

// After an email-code login or a passwordless sign-up: the same continuation
// as a password login, checkout included (it carries on to payment in place)
const continueAfterAccountLogin = (dispatch, payload, props, created) => {
  const { isMobile, inCheckout } = props
  const subscription = _.has(payload, 'user.subscription')
    ? payload.user.subscription
    : {}

  if (
    inCheckout &&
    hasProperty(payload, 'user.role') &&
    payload.user.role === 'free_library_subscriber'
  ) {
    navigate('/')
    toastError(notifications.unableToPurchase)
  } else afterLoginCallback(dispatch, props, subscription, inCheckout)
  if (isMobile) scroller.scrollTo('shipTo', { offset: 64, smooth: true })
  toastSuccess(created ? notifications.signup : notifications.login)
  if (subscription) sendUTMCallback(dispatch)
}

// Verify the entered code. Success resolves the SAME login-success action as
// a password login (see verifyOtp in ./index), so this mirrors dispatchLogin's
// post-login block verbatim — checkout continuity included (FR-5). Resolves
// { success, payload } so the entry screen can render inline error states.
export const dispatchVerifyOtp = (dispatch, { email, code }, props = {}) => {
  const exchange = MOCK_OTP_AUTH
    ? (() => {
        const action =
          code === OTP_MOCK_VALID_CODE
            ? loginRequestSuccess(otpMockVerifyResponse(props.flow))
            : otpVerifyFailure(otpMockVerifyFailure)
        // fetchFailureMiddleware throws a FormSubmissionError for 400 actions
        // after passing them on; fetchHelper swallows that for real requests,
        // so the mock has to as well or the throw escapes the caller.
        try {
          dispatch(action)
        } catch (error) {
          if (error?.name !== 'FormSubmissionError') throw error
        }
        return Promise.resolve([{ type: 'HIDDEN_FETCHING' }, action])
      })()
    : dispatch(verifyOtp({ email, code }))

  return exchange
    .then((res) => {
      if (res[1].type === types.AUTH_LOGIN_SUCCESS) {
        refreshGa4Capture()
        pushGa4Event(
          'GTMEvent',
          {
            eventCategory: 'account-otp',
            eventAction: 'verified',
            eventLabel: props.trigger || 'login'
          },
          res[1].payload.user.id
        )
        // SPEC-101: from the create-account screen this is the account's
        // creation (or a login, for an email that already had one)
        const created = Boolean(
          props.flow && accountCreatedByCode(props.flow, res[1].payload)
        )
        if (props.flow)
          pushGa4Event(
            'GTMEvent',
            {
              eventCategory: created ? 'account-creation' : 'account-sign-in',
              eventAction: 'form-completion',
              eventLabel: 'email_code',
              method: 'email_code',
              ...accountConsentEventProps(
                props.marketingConsent,
                props.marketingConsentTouched
              )
            },
            res[1].payload.user.id
          )

        continueAfterAccountLogin(dispatch, res[1].payload, props, created)
        return { success: true }
      }
      pushGa4Event('GTMEvent', {
        eventCategory: 'account-otp',
        eventAction: 'failed',
        eventLabel: props.trigger || 'login'
      })
      return {
        success: false,
        // SPEC-119 D3: a valid code on a terminated account
        restricted: isRestrictedAccount(res[1].payload),
        payload: res[1].payload
      }
    })
    .catch(() => {
      // Transport rejection (no backend action to inspect): keep the
      // documented result shape so the entry screen can clear its state.
      toastError(notifications.otpVerify)
      return { success: false }
    })
}

// SPEC-101 FR-1, D6–D10: continue-with-email, for every auth screen.
//   purpose signup (checkout, /register, the login page's "Create one?"):
//     new email      — the backend creates the account at once (no code) and
//                      answers { flow: signup, created: true, token, user }:
//                      the session starts here, with the newsletter answer
//                      applied, and the screen carries on like a login.
//     existing email — a login code is emailed (send_code, D7: even for an
//                      account with a password).
//   purpose login (the login page):
//     unknown email  — nothing is created or sent (flow signup, code_sent
//                      false); the page asks "Create one?" first.
//     existing email — a code when the account has no password; with one,
//                      code_sent is false unless send_code ("Email me a code
//                      instead") and the page asks for the password.
// Resolves { created } or { sent, flow, hasPassword }.
export const dispatchRequestAccountCode = (
  dispatch,
  {
    email,
    recaptcha,
    marketingConsent,
    marketingConsentTouched = false,
    signupSurface,
    purpose = 'signup',
    sendCode = true
  },
  props = {}
) => {
  const exchange = MOCK_OTP_AUTH
    ? (() => {
        const success = otpRequestSuccess(
          otpMockAccountRequestResponse(email, { purpose, sendCode })
        )
        dispatch(success)
        return Promise.resolve([{ type: 'HIDDEN_FETCHING' }, success])
      })()
    : dispatch(
        requestOtp(recaptcha)({
          email,
          purpose,
          ...(sendCode && { sendCode: true }),
          ...(purpose === 'signup' &&
            accountConsentPayload(marketingConsent, marketingConsentTouched)),
          // SPEC-117: a new email is created by this request, so the surface
          // travels here; an existing email goes on to a login code
          ...(purpose === 'signup' && signupSurfacePayload(signupSurface))
        })
      )

  return exchange
    .then(([_, res]) => {
      if (
        res.type === types.AUTH_OTP_REQUEST_SUCCESS &&
        res.payload?.created &&
        res.payload?.token
      ) {
        const { token, user } = res.payload
        dispatch(loginRequestSuccess({ token, user }))
        refreshGa4Capture()
        pushGa4Event(
          'GTMEvent',
          {
            eventCategory: 'account-creation',
            eventAction: 'form-completion',
            eventLabel: 'email',
            method: 'email',
            ...accountConsentEventProps(
              marketingConsent,
              marketingConsentTouched
            )
          },
          user.id
        )
        continueAfterAccountLogin(dispatch, { token, user }, props, true)
        return { created: true }
      }
      if (res.type === types.AUTH_OTP_REQUEST_SUCCESS) {
        const flow = res.payload?.flow === 'login' ? 'login' : 'signup'
        const sent = res.payload?.codeSent !== false
        if (sent)
          pushGa4Event('GTMEvent', {
            eventCategory: 'account-otp',
            eventAction: 'requested',
            eventLabel: purpose === 'login' ? 'login' : 'create-account'
          })
        return { sent, flow, hasPassword: Boolean(res.payload?.hasPassword) }
      }
      if (res?.payload?.status === 412) {
        toastError(notifications.recaptcha)
        dispatchDisplayRecaptcha(dispatch)
      } else toastFailure(notifications.otpRequest, res.payload)
      return { sent: false }
    })
    .catch(() => {
      toastError(notifications.otpRequest)
      return { sent: false }
    })
    .finally(() => dispatch(clearRecaptchaToken()))
}
